import { Injectable, inject } from "@angular/core";
import { NavigationEnd, Router } from "@angular/router";
import { BehaviorSubject, distinctUntilChanged, Observable, switchMap } from "rxjs";
import { Currency, CurrencyId } from "../models/currency";
import { CurrencyConversionService } from "./currency-conversion.service";
import { CSP } from "../models/csp-api";
import { LoggerService } from "./logger.service";

type CurrencyStateKey = CSP | "azure_mca" | "azure_ca";

/**
 * CurrencyStateService manages currency state per Cloud Service Provider (CSP).
 *
 * Each CSP has a default currency:
 * - AWS: USD
 * - GCP: USD
 * - Azure: GBP
 * - OCI: EUR
 * - DRCC: EUR
 *
 * Users can temporarily override the default currency for the current page,
 * but the application resets to the CSP default on navigation and reload.
 */
@Injectable({
  providedIn: "root",
})
export class CurrencyStateService {
  private conversion = inject(CurrencyConversionService);
  private log = inject(LoggerService);
  private router = inject(Router);
  private readonly activeCsp$ = new BehaviorSubject<CSP>("aws");

  private readonly CURRENCY_PREFS_KEY = "currency";
  private hasInitializedStorage = false;
  private hasInitializedRouteSync = false;

  private readonly currencyKeysByCsp: Record<CSP, CurrencyStateKey[]> = {
    aws: ["aws"],
    gcp: ["gcp"],
    azure: ["azure", "azure_mca", "azure_ca"],
    oci: ["oci"],
    drcc: ["drcc"],
  };

  public readonly defaultPreferences: Record<CurrencyStateKey, CurrencyId> = {
    aws: "USD",
    gcp: "USD",
    azure: "GBP",
    azure_mca: "USD",
    azure_ca:"USD",
    oci: "EUR",
    drcc: "EUR",
  };

  // CSP-specific currency state
  private readonly currencyState = new Map<CurrencyStateKey, BehaviorSubject<Currency>>([
    ["aws", new BehaviorSubject(this.getInitialCurrency("USD", "aws"))],
    ["gcp", new BehaviorSubject(this.getInitialCurrency("USD", "gcp"))],
    ["azure", new BehaviorSubject(this.getInitialCurrency("GBP", "azure"))],
    ["azure_mca", new BehaviorSubject(this.getInitialCurrency("USD", "azure_mca"))],
    ["azure_ca", new BehaviorSubject(this.getInitialCurrency("USD", "azure_ca"))],
    ["oci", new BehaviorSubject(this.getInitialCurrency("EUR", "oci"))],
    ["drcc", new BehaviorSubject(this.getInitialCurrency("EUR", "drcc"))],
  ]);

  /**
   * Get the storage key for a specific CSP
   */
  /**
   * Load all currency preferences from localStorage
   */
  private loadAllPreferences(): Partial<Record<CurrencyStateKey, CurrencyId>> {
    try {
      const stored = localStorage.getItem(this.CURRENCY_PREFS_KEY);
      if (stored) {
        return JSON.parse(stored);
      }
    } catch (e) {
      this.log.error("Failed to parse currency preferences, using defaults", e);
    }
    return {};
  }

  /**
   * Save all currency preferences to localStorage
   */
  private saveAllPreferences(prefs: Record<CurrencyStateKey, CurrencyId>): void {
    try {
      localStorage.setItem(this.CURRENCY_PREFS_KEY, JSON.stringify(prefs));
    } catch (e) {
      this.log.error("Failed to save currency preferences", e);
    }
  }

  /**
   * Migrate old format (currency-csp keys) to new format (single JSON key)
   */
  private migrateOldFormat(): void {
    const csps: CurrencyStateKey[] = ["aws", "azure", "azure_mca", "gcp", "oci", "drcc"];
    const oldKeys = csps
      .map((csp) => ({
        csp,
        val: localStorage.getItem(`currency-${csp}`),
      }))
      .filter(({ val }) => val !== null);

    if (oldKeys.length > 0) {
      const prefs = Object.fromEntries(oldKeys.map(({ csp, val }) => [csp, val])) as Record<
        CurrencyStateKey,
        CurrencyId
      >;

      this.saveAllPreferences(prefs);

      // Remove old keys
      oldKeys.forEach(({ csp }) => {
        localStorage.removeItem(`currency-${csp}`);
      });

      this.log.info(`Migrated ${oldKeys.length} currency preferences to new format`);
    }
  }

  /**
   * Get the initial currency from localStorage or the default for a CSP
   */
  private getInitialCurrency(defaultCurrencyId: CurrencyId, csp: CurrencyStateKey): Currency {
    return this.conversion.getCurrency(defaultCurrencyId, defaultCurrencyId);
  }

  /**
   * Get currency state as Observable for a specific CSP
   */
  public getCurrency$(csp: CurrencyStateKey): Observable<Currency> {
    return (this.currencyState.get(csp) ?? this.currencyState.get("aws"))!.asObservable();
  }

  /**
   * Get the current currency value for a CSP (synchronous)
   */
  public getCurrency(csp: CurrencyStateKey): Currency {
    return (this.currencyState.get(csp) ?? this.currencyState.get("aws"))!.getValue();
  }

  public setActiveCsp(csp: CSP): void {
    this.activeCsp$.next(csp);
  }

  public getActiveCsp(): CSP {
    return this.activeCsp$.getValue();
  }

  /**
   * Get currency state as Observable for the currently active CSP
   */
  public getActiveCurrency$(): Observable<Currency> {
    return this.activeCsp$.pipe(
      distinctUntilChanged(),
      switchMap((csp) => this.getCurrency$(csp)),
      distinctUntilChanged((prev, curr) => prev?.id === curr?.id),
    );
  }

  /**
   * Get the current currency value for the currently active CSP
   */
  public getActiveCurrencyValue(): Currency {
    return this.getCurrency(this.getActiveCsp());
  }

  /**
   * Change currency for a specific CSP only
   * Updates localStorage (per-CSP key) and that CSP's state only
   */
  public changeCurrency(csp: CurrencyStateKey, currencyId: CurrencyId, persist: boolean = true): void {
    this.log.info(`Changing currency for ${csp} to ${currencyId} (persist=false)`);

    // Update state for the specific CSP only
    this.updateCurrencyForCsp(csp, currencyId);
  }

  /**
   * Update currency state for a specific CSP
   */
  private updateCurrencyForCsp(csp: CurrencyStateKey, currencyId: CurrencyId): void {
    const defaultForCsp = this.getDefaultCurrencyForCsp(csp);
    const newCurrency = this.conversion.getCurrency(currencyId, defaultForCsp);
    this.currencyState.get(csp)?.next(newCurrency);
  }

  public resetCurrencyToDefault(csp: CurrencyStateKey, clearPersistedPreference: boolean = false): void {
    const defaultCurrencyId = this.getDefaultCurrencyForCsp(csp);

    if (clearPersistedPreference) {
      const prefs = this.loadAllPreferences();
      delete prefs[csp];
      this.saveAllPreferences(this.normalizePreferencesObject(prefs));
    }

    this.updateCurrencyForCsp(csp, defaultCurrencyId);
  }

  public resetCurrenciesForCsp(csp: CSP, clearPersistedPreference: boolean = false): void {
    const currencyKeys = this.currencyKeysByCsp[csp] ?? [csp];

    for (const currencyKey of currencyKeys) {
      this.resetCurrencyToDefault(currencyKey, clearPersistedPreference);
    }
  }

  /**
   * Get the default currency for a CSP
   */
  private getDefaultCurrencyForCsp(csp: CurrencyStateKey): CurrencyId {
    const defaults: Record<CurrencyStateKey, CurrencyId> = {
      aws: "USD",
      gcp: "USD",
      azure: "GBP",
      azure_mca: "USD",
      azure_ca: "USD",
      oci: "EUR",
      drcc: "EUR",
    };
    return defaults[csp] ?? "USD";
  }

  private isCurrencyId(value: unknown): value is CurrencyId {
    return value === "USD" || value === "GBP" || value === "EUR";
  }

  private isCsp(value: string): value is CSP {
    return value === "aws" || value === "gcp" || value === "azure" || value === "oci" || value === "drcc";
  }

  private getCurrentPath(): string {
    return typeof location === "undefined" ? "" : `${location.pathname}${location.search}`;
  }

  private extractCspFromUrl(url: string): CSP | null {
    const segments = url
      .split("?")[0]
      .split("/")
      .map((segment) => segment.toLowerCase())
      .filter(Boolean);

    const csp = segments.find((segment) => this.isCsp(segment));
    return csp ?? null;
  }

  private syncCurrencyToRoute(url: string): void {
    const csp = this.extractCspFromUrl(url);

    if (!csp) {
      return;
    }

    this.setActiveCsp(csp);
    this.resetCurrenciesForCsp(csp, true);
  }

  private initializeRouteSync(): void {
    if (this.hasInitializedRouteSync) {
      return;
    }

    this.hasInitializedRouteSync = true;
    this.syncCurrencyToRoute(this.getCurrentPath());

    this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.syncCurrencyToRoute(event.urlAfterRedirects);
      }
    });
  }

  private clearPersistedCurrencyPreferences(): void {
    this.saveAllPreferences({ ...this.defaultPreferences });
  }

  private normalizePreferencesObject(raw: unknown): Record<CurrencyStateKey, CurrencyId> {
    const normalized: Record<CurrencyStateKey, CurrencyId> = {
      ...this.defaultPreferences,
    };

    if (!raw || typeof raw !== "object") {
      return normalized;
    }

    const candidate = raw as Partial<Record<CurrencyStateKey, unknown>>;
    const csps = Object.keys(this.defaultPreferences) as CurrencyStateKey[];

    for (const csp of csps) {
      const value = candidate[csp];
      if (this.isCurrencyId(value)) {
        normalized[csp] = value;
      }
    }

    return normalized;
  }

  private ensureStorageInitialized(): void {
    if (this.hasInitializedStorage) {
      return;
    }

    this.migrateOldFormat();

    const raw = localStorage.getItem(this.CURRENCY_PREFS_KEY);
    const fallback = { ...this.defaultPreferences };

    if (!raw) {
      this.saveAllPreferences(fallback);
      this.hasInitializedStorage = true;
      return;
    }

    try {
      const parsed = JSON.parse(raw);

      if (typeof parsed === "string" && this.isCurrencyId(parsed)) {
        const migrated = { ...fallback, aws: parsed };
        this.saveAllPreferences(migrated);
        this.log.info("Migrated legacy currency string to object format");
      } else {
        const normalized = this.normalizePreferencesObject(parsed);
        this.saveAllPreferences(normalized);
      }
    } catch (e) {
      const legacy = raw.replace(/^"|"$/g, "");
      if (this.isCurrencyId(legacy)) {
        const migrated = { ...fallback, aws: legacy };
        this.saveAllPreferences(migrated);
        this.log.info("Migrated legacy raw currency value to object format");
      } else {
        this.log.error("Invalid currency storage format, resetting to defaults", e);
        this.saveAllPreferences(fallback);
      }
    }

    this.hasInitializedStorage = true;
  }

  public initializeOnAppStart(): void {
    this.ensureStorageInitialized();
    this.clearPersistedCurrencyPreferences();
    this.initializeRouteSync();
  }
}
